import React, { useState } from "react"
import styled from "@emotion/styled"
import { rgba } from "polished"
import Section from "src/components/Section"
import Grid from "src/components/Grid"
import Image from "src/components/Image"
import Link from "src/components/Link"
import Checkbox from "src/components/Checkbox"
import { globals, typography, colors, mq } from "src/styles"
import { getSlugLink } from "src/utils/format"

const Wrapper = styled(Section)`
  ${({ vertical }) =>
    `
    ${globals.verticalSpacing("padding-top", vertical)}
    ${globals.verticalSpacing("padding-bottom", vertical)}
    `}
  ${({ horizontal }) =>
    `
    ${globals.verticalSpacing("padding-left", horizontal)}
    ${globals.verticalSpacing("padding-right", horizontal)}
  `}
  ${mq.mediumAndBelow} {
    ${({ hideMobile }) => (hideMobile && "display: none")}
  }
  ${mq.largeAndUp} {
    ${({ hideDesktop }) => (hideDesktop && "display: none")}
  }
`

const PartnerCard = styled(globals.Card)`
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: stretch;
  background: ${colors.white};
  ${({ isLink }) =>
    isLink
      ? `
    &:hover {
      box-shadow: 0 4px 10px ${rgba(colors.textColor, 0.25)};
    }
  `
      : ""}
  .gatsby-image-wrapper {
    flex-grow: 0;
    flex-shrink: 0;
  }
  .gatsby-image-wrapper,
  .gatsby-image-wrapper img {
    border-radius: var(--card-border-radius) var(--card-border-radius) 0 0;
  }
`

const FilterCol = styled.div`
  h5 {
    ${typography.articleTitleSmall}
    margin: 0 0 .75em;
  }
`

const CardText = styled.div`
  padding: 16px 20px 20px 20px;
  display: flex;
  flex-direction: column;
  align-items: stretch;
  justify-content: space-between;
  flex-grow: 1;
  h5 {
    ${typography.bodyMedium}
    font-weight: ${typography.bold};
    margin: 0 0 .5em;
  }
  p {
    margin: 0;
  }
`

const FilterList = styled.ul`
  list-style: none;
  padding: 0;
  ${mq.largeAndBelow} {
    columns: 3;
    column-gap: 20px;
  }
  ${mq.mediumAndBelow} {
    columns: 2;
  }
  li {
    margin: 0;
    padding: 0;
    label {
      padding: 0.3em 0;
      &:not(.checked):hover {
        .checkbox {
          color: red;
          box-shadow: ${globals.shadowLift()};
        }
        svg {
          color: ${rgba(colors.lightTextColor, 0.5)};
        }
      }
      .label-text {
        line-height: 1em;
      }
    }
  }
`

const PartnerList = ({
  className,
  nextTheme,
  prevTheme,
  isFirstSection,
  partners,
  id,
  hideMobile,
  hideDesktop,

  spacing,
}) => {
  const [filteredPartners, setFilteredPartners] = useState(["Featured"])
  let allTags = []

  partners.forEach(partner => {
    allTags.push(partner.tags)
  })

  allTags = allTags.flat()
  allTags.unshift("Featured")
  const filterList = [...new Set(allTags)]

  const onChange = event => {
    const value = event?.target?.value
    if (filteredPartners.includes(value)) {
      setFilteredPartners(filteredPartners.filter(partner => partner !== value))
    } else {
      setFilteredPartners(filteredPartners.concat(value))
    }
  }

  return (
    <Wrapper
      className={className}
      prevTheme={prevTheme}
      nextTheme={nextTheme}
      isFirstSection={isFirstSection}
      id={id}
      vertical={spacing?.vertical}
      horizontal={spacing?.horizontal}
      hideMobile={hideMobile}
      hideDesktop={hideDesktop}
    >
      <Grid
        small="m [1] m"
        medium="1 [12] 1"
        large="1 [3] 1 [8] 1"
        vAlign="top"
        rowGap={["40px", "40px", "40px"]}
      >
        <FilterCol>
          <h5>Filter Partners</h5>
          <FilterList>
            {filterList.map((item, index) => {
              const active = filteredPartners.includes(item)
              return (
                <li>
                  <Checkbox value={item} onChange={onChange} checked={active}>
                    {item}
                  </Checkbox>
                </li>
              )
            })}
          </FilterList>
        </FilterCol>
        <div>
          <Grid
            small="[1] [1]"
            medium="[1] [1]"
            extraLarge="[1] [1] [1]"
            colGap={["20px", "20px", "30px"]}
            rowGap={["20px", "20px", "30px"]}
            vAlign="stretch"
          >
            {partners.map((partner, index) => {
              const inFilter = partner.tags.some(
                r => filteredPartners.indexOf(r) >= 0
              )
              if (!inFilter) {
                return false
              }
              const isLink = partner?.link?.externalLink || partner?.link?.link
              return (
                <div>
                  <PartnerCard
                    key={partner._key}
                    as={isLink ? Link : "div"}
                    isLink={isLink}
                    to={
                      partner?.link?.type === "externalLink"
                        ? partner?.link?.externalLink
                        : getSlugLink(
                            partner?.link?.link,
                            false,
                            partner?.link?.linkSection
                          )
                    }
                    external={
                      partner?.link?.type === "externalLink" ||
                      partner?.link?.newTab
                    }
                    target={partner?.link?.newTab ? "_blank" : ""}
                    title={partner?.link?.title || "View " + partner.title}
                    name={partner?.link?.title || "View " + partner.title}
                  >
                    {partner?.image?.asset?.gatsbyImageData && (
                      <Image
                        ratio={9 / 16}
                        image={partner.image.asset.gatsbyImageData}
                        loading="eager"
                        sizes={"600px"}
                      />
                    )}
                    <CardText>
                      <h5>{partner.description}</h5>
                      <p>{partner.title}</p>
                    </CardText>
                  </PartnerCard>
                </div>
              )
            })}
          </Grid>
        </div>
      </Grid>
    </Wrapper>
  )
}

export default PartnerList
